import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Search, EyeOff } from "lucide-react";
import { api, toast } from "../../App";

const fullName = (u) => `${u.first_name || ""} ${u.last_name || ""}`.trim() || "Unnamed";

const PersonRow = ({ u, checked, onToggle }) => (
  <label className="flex items-center gap-3 px-3 py-2 rounded-md3-sm hover:bg-md3-surface-container cursor-pointer transition-colors" data-testid={`visibility-row-${u.id}`}>
    <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`visibility-check-${u.id}`} />
    <span className="flex-1 text-sm">{fullName(u)}</span>
    <Badge variant="outline" className="text-[10px]">{u.department}{u.access_level ? ` · ${u.access_level}` : ""}</Badge>
  </label>
);

export const SalesVisibilityDialog = ({ open, onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(new Set());
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ("");
    api.get("/sales/visibility-settings")
      .then((r) => { setData(r.data); setHidden(new Set(r.data.hidden_user_ids)); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load settings"));
  }, [open]);

  const toggle = (id) => setHidden((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const save = async () => {
    try {
      setSaving(true);
      await api.put("/sales/visibility-settings", { hidden_user_ids: [...hidden] });
      toast.success("Sales visibility saved");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const people = (data?.candidates || []).filter((u) => fullName(u).toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white" data-testid="sales-visibility-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><EyeOff className="h-5 w-5" /> Hidden from Sales Managers</DialogTitle>
          <p className="text-sm text-gray-600">
            Sales Managers won't see estimations, cards, follow-ups or notes for the ticked salespeople. Management users always see everything.
          </p>
          {data && (
            <p className="text-xs text-gray-500" data-testid="sales-visibility-meta">
              {data.is_default ? "Default: all Management users are hidden until you save." : `Last saved by ${data.updated_by_name || "Admin"} on ${(data.updated_at || "").slice(0, 10)}`}
            </p>
          )}
        </DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search salespeople..." className="pl-8" data-testid="sales-visibility-search" />
        </div>
        <div className="max-h-80 overflow-y-auto border rounded-md3-md p-1">
          {!data ? <p className="p-4 text-sm text-gray-500">Loading...</p> : people.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">No matching salespeople</p>
          ) : people.map((u) => <PersonRow key={u.id} u={u} checked={hidden.has(u.id)} onToggle={() => toggle(u.id)} />)}
        </div>
        <DialogFooter className="items-center">
          <span className="text-xs text-gray-600 mr-auto" data-testid="sales-visibility-count">{hidden.size} hidden</span>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !data} className="bg-md3-primary text-md3-on-primary" data-testid="sales-visibility-save">
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
